import { buildInteractiveResult } from '@coveo/headless';
import { useGeolocationState } from 'contexts/GeolocationContext';
import * as customPropTypes from 'custom-prop-types';
import PropTypes from 'prop-types';
import React from 'react';

import { Button } from '@/design-system/atoms/Button';
import { Loading } from '@/design-system/atoms/Loading';
import { Map } from '@/design-system/atoms/Map';
import { LocationDetail } from '@/design-system/molecules/LocationDetail';
import { useTimeOfDay } from '@/hooks/useTimeOfDay';
import { appendDmParameters } from '@/utils/dynamicMedia';
import { mappingWorkingHours } from '@/utils/mappingWorkingHours';

import {
    useLocationSearchContext,
    useLocationSearchZipCodeErrorContext,
} from '../../contexts/LocationSearchContext';
import { useLocationUIContext } from '../../contexts/LocationUIContext';
import { useLocationViewState } from '../../hooks/useLocationViewState';
import isInMarketLocation from '../../utils/isInMarketLocation';
import { isVpcBannerQualifyingSearch } from '../../utils/isVpcQualifyingSearch';
import styles from './LocationResultsList.module.scss';

const getLocationType = (result) => {
    return result.raw.loctype ?? result.raw.locationType ?? result.raw.locvariant ?? '';
};

const LocationResultsList = ({ cta, analytics }) => {
    const {
        coveo: {
            engine,
            controller: { resultList, querySummary, facet },
        },
        noResultsComponent,
        errorComponent,
        vpcBannerComponent,
        fetchAllMapResults,
        searchResultsDmParameters,
    } = useLocationSearchContext();

    const phase = useTimeOfDay();
    const geo = useGeolocationState();
    const [specialtyValues, setSpecialtyValues] = React.useState([]);

    const buildImageSrc = React.useCallback(
        (url) => appendDmParameters(url, searchResultsDmParameters),
        [searchResultsDmParameters],
    );

    const { googleMaps } = useLocationUIContext();

    const viewState = useLocationViewState();

    const zipCodeServerError = useLocationSearchZipCodeErrorContext();

    const [resultListState, setResultListState] = React.useState({});
    const [results, setResults] = React.useState([]);
    const [mapResults, setMapResults] = React.useState([]);
    const [isLoadingMapResults, setIsLoadingMapResults] = React.useState(false);
    const [querySummaryState, setQuerySummaryState] = React.useState(querySummary.state);

    React.useEffect(() => {
        return resultList.subscribe(() => {
            setResultListState(resultList.state);

            const newResults = resultList.state.results.map((s) => {
                const interactiveResultController = engine
                    ? buildInteractiveResult(engine, {
                          options: { result: s },
                      })
                    : null;

                let parsedWorkingHours = null;
                let parsedAdjustedHours = null;

                try {
                    parsedWorkingHours = s.raw.locworkinghours && JSON.parse(s.raw.locworkinghours);
                } catch (e) {
                    console.error(e);
                }

                try {
                    parsedAdjustedHours =
                        s.raw.locadjustedhours && JSON.parse(s.raw.locadjustedhours);
                } catch (e) {
                    console.error(e);
                }

                let workingHours = mappingWorkingHours(
                    parsedWorkingHours,
                    parsedAdjustedHours,
                    `${s.raw.locopen24hours}`.toLowerCase() === 'true',
                );

                for (const key in workingHours) {
                    const value = workingHours[key];
                    workingHours[key] = {
                        ...value,
                        closed: `${value.closed}`.toLowerCase() === 'true',
                    };
                }
                const temporarilyClosed = `${s.raw.loctemporarilyclosed}`.toLowerCase() === 'true';

                let image;
                if (s.raw.locimgurl) {
                    image = {
                        defaultImgSrc: buildImageSrc(s.raw.locimgurl),
                    };
                }

                let address;
                if (
                    [
                        s.raw.locaddrline1,
                        s.raw.locaddrline2,
                        s.raw.locaddrline3?.split(',')[0],
                        s.raw.locationstate?.[0],
                        s.raw.locationzip?.[0],
                        s.raw.locmapurl,
                        s.raw.locaddressoverridemessage,
                    ].filter(Boolean).length > 0
                ) {
                    address = {
                        line1: s.raw.locaddrline1,
                        line2: s.raw.locaddrline2,
                        city: s.raw.locaddrline3?.split(',')[0],
                        state: s.raw.locationstate?.[0],
                        zipCode: s.raw.locationzip?.[0],
                        googleMapsUrl: s.raw.locmapurl,
                        addressOverrideMessage: s.raw.locaddressoverridemessage,
                    };
                }

                const content = {
                    variant: s.raw.locvariant,
                    locationType: getLocationType(s),
                    id: s.uniqueId,
                    title: s.raw.locname || s.title,
                    buildingImage: image,
                    parentLocationName: s.raw.locparent,
                    rootLocationName: s.raw.locroot,
                    buildingName: s.raw.locbuildingname,
                    address,
                    phoneNumber: s.raw.locphnum1,
                    cta: {
                        label: 'View Location Details',
                        href: s.uri,
                    },
                    workingHours,
                    temporarilyClosed,
                    overrideMessage: s.raw.locoverridemessage,
                    waitTime: `${s.raw.locwaittime}`.toLowerCase() === 'true',
                    epicDepartmentId: s.raw.locepicdepartmentid,
                    position: {
                        lat: s.raw.latitude,
                        lng: s.raw.longitude,
                    },
                    coveoAnalyticsCallbacks: interactiveResultController
                        ? {
                              cancelPendingSelect: () =>
                                  interactiveResultController.cancelPendingSelect(),
                              onClick: () => interactiveResultController.select(),
                              onContextMenu: () => interactiveResultController.select(),
                              onMouseDown: () => interactiveResultController.select(),
                              onMouseUp: () => interactiveResultController.select(),
                              onTouchStart: () => interactiveResultController.beginDelayedSelect(),
                              onTouchEnd: () => interactiveResultController.cancelPendingSelect(),
                          }
                        : undefined,
                };

                return {
                    ...content,
                    content,
                };
            });

            setResults(newResults);
        });
    }, [resultList, engine, buildImageSrc]);

    React.useEffect(() => {
        const unsubscribe = querySummary.subscribe(() => {
            setQuerySummaryState(querySummary.state);
        });
        return () => {
            // Unsubscribe from the query summary state
            unsubscribe();
        };
    }, [querySummary]);

    // null-safe: facet.specialty is absent from the context defaultValue
    React.useEffect(() => {
        if (!facet?.specialty) return;
        return facet.specialty.subscribe(() => {
            setSpecialtyValues(facet.specialty.state.values ?? []);
        });
    }, [facet?.specialty]);

    const [treatmentValues, setTreatmentValues] = React.useState([]);

    // Track treatment facet separately — 'Virtual Care' is a qualifying value that enables the
    // banner OR-independently, so treatment needs its own value array (not just a boolean flag).
    React.useEffect(() => {
        if (!facet?.treatment) return;
        return facet.treatment.subscribe(() => {
            setTreatmentValues(facet.treatment.state.values ?? []);
        });
    }, [facet?.treatment]);

    const [hasOtherSelectedFacets, setHasOtherSelectedFacets] = React.useState(false);

    // Track facets whose selections have no qualifying values of their own (type, condition,
    // accepted insurance). A selection in any of these suppresses the zero-state banner gate.
    // Treatment is excluded here — it is handled via treatmentValues above.
    React.useEffect(() => {
        const nonQualifyingFacets = [
            facet?.type,
            facet?.condition,
            facet?.acceptedInsurance,
        ].filter(Boolean);
        if (!nonQualifyingFacets.length) return;

        const update = () => {
            setHasOtherSelectedFacets(
                nonQualifyingFacets.some((f) => f.state.values.some((v) => v.state === 'selected')),
            );
        };

        const unsubscribers = nonQualifyingFacets.map((f) => f.subscribe(update));
        return () => unsubscribers.forEach((u) => u());
    }, [facet?.type, facet?.condition, facet?.acceptedInsurance]);

    const bannerPosition = React.useMemo(() => {
        if (phase === null) return null;
        if (phase === 'night') return 0;
        return Math.min(2, results.length);
    }, [phase, results.length]);

    // Fetch all results for map view when switching to maps or when search changes
    React.useEffect(() => {
        if (viewState !== 'maps') {
            setMapResults([]);
            return;
        }

        // Only fetch if we have results in the regular list (indicating a search was done)
        if (resultListState.searchResponseId && !resultListState.isLoading) {
            setIsLoadingMapResults(true);
            fetchAllMapResults()
                .then((rawResults) => {
                    // Transform raw results to match the expected format
                    const transformedResults = rawResults.map((s) => {
                        let parsedWorkingHours = null;
                        let parsedAdjustedHours = null;

                        try {
                            parsedWorkingHours =
                                s.raw.locworkinghours && JSON.parse(s.raw.locworkinghours);
                        } catch (e) {
                            console.error(e);
                        }

                        try {
                            parsedAdjustedHours =
                                s.raw.locadjustedhours && JSON.parse(s.raw.locadjustedhours);
                        } catch (e) {
                            console.error(e);
                        }

                        let workingHours = mappingWorkingHours(
                            parsedWorkingHours,
                            parsedAdjustedHours,
                            `${s.raw.locopen24hours}`.toLowerCase() === 'true',
                        );

                        for (const key in workingHours) {
                            const value = workingHours[key];
                            workingHours[key] = {
                                ...value,
                                closed: `${value.closed}`.toLowerCase() === 'true',
                            };
                        }
                        const temporarilyClosed =
                            `${s.raw.loctemporarilyclosed}`.toLowerCase() === 'true';

                        let image;
                        if (s.raw.locimgurl) {
                            image = {
                                defaultImgSrc: buildImageSrc(s.raw.locimgurl),
                            };
                        }

                        let address;
                        if (
                            [
                                s.raw.locaddrline1,
                                s.raw.locaddrline2,
                                s.raw.locaddrline3?.split(',')[0],
                                s.raw.locationstate?.[0],
                                s.raw.locationzip?.[0],
                                s.raw.locmapurl,
                                s.raw.locaddressoverridemessage,
                            ].filter(Boolean).length > 0
                        ) {
                            address = {
                                line1: s.raw.locaddrline1,
                                line2: s.raw.locaddrline2,
                                city: s.raw.locaddrline3?.split(',')[0],
                                state: s.raw.locationstate?.[0],
                                zipCode: s.raw.locationzip?.[0],
                                googleMapsUrl: s.raw.locmapurl,
                                addressOverrideMessage: s.raw.locaddressoverridemessage,
                            };
                        }

                        const content = {
                            variant: s.raw.locvariant,
                            locationType: getLocationType(s),
                            id: s.uniqueId,
                            title: s.raw.locname || s.title,
                            buildingImage: image,
                            parentLocationName: s.raw.locparent,
                            rootLocationName: s.raw.locroot,
                            buildingName: s.raw.locbuildingname,
                            address,
                            phoneNumber: s.raw.locphnum1,
                            cta: {
                                label: 'View Location Details',
                                href: s.uri,
                            },
                            workingHours,
                            temporarilyClosed,
                            overrideMessage: s.raw.locoverridemessage,
                            waitTime: `${s.raw.locwaittime}`.toLowerCase() === 'true',
                            epicDepartmentId: s.raw.locepicdepartmentid,
                            position: {
                                lat: s.raw.latitude,
                                lng: s.raw.longitude,
                            },
                        };

                        return {
                            ...content,
                            content,
                        };
                    });

                    // Map-only filter: virtual locations are excluded from the map layer.
                    // The list result set (`results`, built above) intentionally keeps them.
                    const mapOnlyResults = transformedResults.filter(
                        (item) => item.locationType?.toLowerCase() !== 'virtual',
                    );
                    setMapResults(mapOnlyResults);
                })
                .catch((error) => {
                    console.error('Error fetching map results:', error);
                    setMapResults([]);
                })
                .finally(() => {
                    setIsLoadingMapResults(false);
                });
        }
    }, [
        viewState,
        resultListState.searchResponseId,
        resultListState.isLoading,
        fetchAllMapResults,
        buildImageSrc,
    ]);

    const handleLoadMoreClick = React.useCallback(
        (e) => {
            e.preventDefault();

            resultList.fetchMoreResults();
        },
        [resultList],
    );

    const state = [
        { state: 'error', valid: resultListState.hasError || zipCodeServerError },
        {
            state: 'loading',
            valid: resultListState.isLoading || (viewState === 'maps' && isLoadingMapResults),
        },
        {
            state: 'noResults',
            valid:
                viewState === 'maps'
                    ? mapResults.length === 0 && !isLoadingMapResults
                    : results.length === 0,
        },
    ].find(({ valid }) => valid)?.state;

    const showResults = viewState === 'maps' ? mapResults.length > 0 : results.length > 0;

    if (!state && showResults === false) {
        return null;
    }

    const specialties = specialtyValues.filter((v) => v.state === 'selected').map((v) => v.value);
    const treatments = treatmentValues.filter((v) => v.state === 'selected').map((v) => v.value);

    const shouldShowVpcBanner =
        !!vpcBannerComponent &&
        bannerPosition !== null &&
        isVpcBannerQualifyingSearch({
            term: querySummaryState.query,
            specialties,
            treatments,
            hasOtherFacetSelections: hasOtherSelectedFacets,
        }) &&
        isInMarketLocation(geo) &&
        !resultListState.isLoading &&
        !resultListState.hasError &&
        !zipCodeServerError &&
        results.length > 0 &&
        viewState !== 'maps';

    const renderedResults =
        showResults && viewState === 'result-list'
            ? results.map((r) => (
                  <li key={r.id}>
                      <LocationDetail
                          {...r}
                          variant={LocationDetail.VARIANT.LIST_ITEM}
                          analytics={analytics}
                      />
                  </li>
              ))
            : [];
    if (shouldShowVpcBanner) {
        renderedResults.splice(
            bannerPosition,
            0,
            <li key="vpc-banner" className={styles['location-results-list__vpc-banner']}>
                {vpcBannerComponent}
            </li>,
        );
    }

    return (
        <>
            <div className={styles['location-results-list']}>
                {showResults && viewState === 'result-list' && <ul>{renderedResults}</ul>}

                {showResults && viewState === 'maps' && (
                    <Map
                        items={mapResults}
                        apiKey={googleMaps.apiKey}
                        mapId={googleMaps.mapId}
                        analytics={analytics}
                    />
                )}

                {state === 'error' && <>{errorComponent}</>}

                {state === 'loading' && (
                    <div className={styles['location-results-list__overlay']}>
                        <Loading />
                    </div>
                )}

                {state === 'noResults' && <>{noResultsComponent}</>}
            </div>
            {showResults && resultListState.moreResultsAvailable && viewState === 'result-list' && (
                <div className={styles['location-results-list__load-more']}>
                    <Button
                        label={cta?.label ? cta.label : 'Load More'}
                        ariaLabel={cta?.ariaLabel ? cta.ariaLabel : 'Load More Results'}
                        buttonStyle={Button.STYLE.SECONDARY}
                        onClick={handleLoadMoreClick}
                        disabled={resultListState.isLoading}
                        analytics={{
                            ...analytics,
                            eventName: 'search',
                            componentName: 'Locations Search',
                            searchTerm: querySummaryState.query,
                        }}
                    />
                </div>
            )}
        </>
    );
};

LocationResultsList.propTypes = {
    /**
     * Call to action for loading more results
     */
    cta: PropTypes.shape(Button.propTypes),
    analytics: customPropTypes.analyticsPropType,
};

export default LocationResultsList;
